/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect, FormEvent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { Item, Key } from 'react-stately';
import { useNavigate } from 'react-router';

import { HeroImageSectionFront, Icon, Button, Autosuggest } from 'sarsaparilla';

import { searchLocations } from '../../api';
import { venuePageRoute } from '../../../dev/routesArray';
import type { ParentLocation, TreePermitData } from '../../../types/permit';

const goToSmooth = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState('', '', `#${id}`);
};

interface HeroWithAutoCompleteType {
    locations: Array<Partial<TreePermitData>>;
}

function HeroWithAutoComplete({ locations = [] }: HeroWithAutoCompleteType) {
    const navigate = useNavigate();
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<Array<Partial<TreePermitData>>>([]);
    const [localSuggestions, setLocalSuggestions] = useState<
        Array<Partial<TreePermitData>>
    >([]);
    const [searchSuggestions, setSearchSuggestions] = useState<
        Array<Partial<TreePermitData>>
    >([]);
    const [selectedLocation, setSelectedLocation] = useState('');

    const escapeRegexCharacters = (str: string) => {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    const goToLocation = () => {
        if (!isEmpty(selectedLocation)) {
            navigate(venuePageRoute(selectedLocation));
        }
    };
    const match = (v: any, key: string) =>
        get(v, key, '').toLowerCase().indexOf(value.toLowerCase()) !== -1;

    const getSuggestions = (v: string) => {
        const escapedValue = escapeRegexCharacters(v.trim());

        if (escapedValue === '') {
            return [];
        }

        return locations?.filter((val) => match(val, 'state') || match(val, 'name'));
    };

    const onSuggestionsFetchRequested = (valueNew: { value: string }) => {
        const { value: v } = valueNew;
        const _suggestions = getSuggestions(v);

        setLocalSuggestions(_suggestions);
        setSuggestions(_suggestions);
    };

    const makeSearch = (_value: string) => {
        searchLocations(_value).then(
            ({
                data,
            }: {
                data: {
                    entity_id: string;
                    entity_type: string;
                    fq: Array<string>;
                    latitude: string;
                    location: string;
                    longitude: string;
                    query: string;
                    radius: string;
                    results: Array<ParentLocation>;
                    size: number;
                    spelling_autocorrected: boolean;
                    start: string;
                    total: number;
                };
            }) => {
                const res = get(data, 'results', []);
                const results = res.map((item: Partial<ParentLocation>) => {
                    return {
                        ...item,
                        id: item?.entity_id,
                    };
                });

                setSearchSuggestions(results);
            }
        );
    };

    const onSuggestionsClearRequested = () => {
        setLocalSuggestions([]);
        setSuggestions([]);
    };

    useEffect(() => {
        setSuggestions(uniqBy([...searchSuggestions, ...localSuggestions], 'id'));
    }, [searchSuggestions, localSuggestions]);

    const handleInputChange = (_value: string) => {
        setValue(_value);
        makeSearch(_value);
        onSuggestionsFetchRequested({ value: _value });
    };

    const handleOnSelect = (key: Key, suggestion?: Partial<TreePermitData>) => {
        if (suggestion?.id) {
            setSelectedLocation(suggestion.id);
        }
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        goToLocation();
    };

    const renderSearchComponent = () => {
        return (
            <>
                <div className="tree-permit-splash-wrap-search">
                    <form className="form-search" onSubmit={handleSubmit}>
                        <div className={`search-input-wrapper`}>
                            <div className="form-item-wrap" role="search">
                                <Autosuggest
                                    allowsEmptyCollection
                                    allowsCustomValue
                                    isLabelVisible={false}
                                    menuTrigger="focus"
                                    label="Search"
                                    placeholder="Search by state or forest name"
                                    items={suggestions}
                                    onInputChange={handleInputChange}
                                    onSelect={handleOnSelect}
                                    onClear={onSuggestionsClearRequested}
                                >
                                    {(item) => {
                                        return <Item>{item.name}</Item>;
                                    }}
                                </Autosuggest>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            appearance="secondary"
                            size="lg"
                            className="hero-select-button"
                            gaTrackingId="071089442512"
                            gaTrackingValue={value || 'no value set'}
                        >
                            Get started
                        </Button>
                    </form>
                </div>
                <div className="tree-permit-splash-wrap-watch-list">
                    <Button
                        className="tree-permit-splash-wrap-watch-list-button"
                        iconBeforeElement={<Icon iconName="arrow-bottom" />}
                        appearance="pill"
                        size="xs"
                        onClick={() => goToSmooth('locations')}
                        gaTrackingId="779124299328"
                    >
                        View all Tree Permit Locations
                    </Button>
                </div>
            </>
        );
    };
    return (
        <HeroImageSectionFront
            buttonAppearance="secondary"
            hasUnderline={false}
            title="Get Your Christmas Tree Permit"
            className="tree-permit-splash-header"
            backgroundImagePath="/img/tree-permits/hero/tree-permits-hero"
            backgroundImageAltText="A group of people walking in a snow covered forest surrounded by snow capped mountains"
            backgroundImageColorOverlay="#fff"
            backgroundImageColorOverlayOpacity={0.1}
            render={() => renderSearchComponent()}
        />
    );
}

HeroWithAutoComplete.propTypes = {
    locations: PropTypes.any,
};

export default HeroWithAutoComplete;
